import React, { useCallback, useEffect, useState } from 'react';
import Badge from '@material-ui/core/Badge';
import { Dialog, DialogContent, DialogTitle, makeStyles, Theme, Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import Popover from '@material-ui/core/Popover';
import Updates from './Updates';
import SettingsSolid from '@eduzz/houston-icons/SettingsSolid';
import NotificationOutline from '@eduzz/houston-icons/NotificationOutline';
import { fetchUpdates } from 'redux/updateNotifications/updateNotificationsSlice';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { RootState } from 'redux/store';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles<Theme, { color: string }>((theme) => {
  return {
    dot: {
      padding: 0,
      minWidth: 0,
      top: 5,
      right: 5,
      width: 5,
      height: 5,
      background: theme.defaultTheme.support_palette.red,
    },
    iconNotification: {
      position: 'relative',
      '& .houston-icon': {
        color: (props) => props.color ?? theme.palette.common.white,
      },
    },
    containerIcon: {
      padding: 0,
      margin: '0 12px 0 0',
    },
    paper: {
      padding: '10px 16px',
      '@media (min-width: 600px)': {
        minWidth: 526,
        maxWidth: 526,
      },
      '@media (max-width: 600px)': {
        minWidth: '90vw',
        maxWidth: '90vw',
      },
      maxHeight: 450,
      minHeight: 450,
    },
    arrowPaper: {
      position: 'absolute',
      backgroundColor: 'transparent',
      borderLeft: '10px solid transparent',
      borderRight: '10px solid transparent',
      borderBottom: '10px solid #FFF',
      top: 22,
      left: 0.5,
    },
    headerDialog: {
      '& > h2': {
        display: 'flex',
        alignItems: 'center',
        '& > h6': {
          marginRight: 'auto',
          marginLeft: 'auto',
          paddingRight: 34,
        },
      },
    },
    titleRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    settingsIcon: {
      color: '#a7a6a7',
      cursor: 'pointer',
    },
  };
});

const NotificationCenter = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const updates = useAppSelector((state: RootState) => state.updatesNotification);
  const [openDialog, setOpenDialog] = useState(false);
  const dispatch = useAppDispatch();
  const { header_link_color } = useAppSelector((store) => store.customizations);
  const classes = useStyles({ color: header_link_color });
  const open = Boolean(anchorEl);
  const history = useHistory();

  useEffect(() => {
    dispatch(fetchUpdates());
  }, []);

  const handleClickPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClickDialog = () => {
    setOpenDialog(!openDialog);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isBadgeInvisible = useCallback(() => {
    return updates?.data?.every((u) => !!u.read);
  }, [updates]);

  const handleSettingsClick = () => {
    history.push(`/notificacoes`);
  };

  if (!updates) return null;

  return (
    <div>
      {
        <div className={classes.iconNotification}>
          <IconButton id='notificationCenter' className={classes.containerIcon} onClick={handleClickPopover}>
            <Badge classes={{ dot: classes.dot }} color='secondary' variant='dot' invisible={isBadgeInvisible()}>
              <NotificationOutline size={24} />
            </Badge>
          </IconButton>
          {open && <div className={classes.arrowPaper}></div>}
        </div>
      }

      <Popover
        id='notifications-popover'
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        PaperProps={{
          style: {
            left: 0,
            margin: '8px 0 0 10px',
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div className={classes.paper}>
          <div className={classes.titleRow}>
            <Typography variant='body1'>Notificações</Typography>
            <IconButton id='notifications-link-settings' onClick={handleSettingsClick}>
              <SettingsSolid size={24} className={classes.settingsIcon} />
            </IconButton>
          </div>
          <Updates updates={updates.data} />
        </div>
      </Popover>

      <Dialog fullScreen open={openDialog}>
        <DialogTitle className={classes.headerDialog}>
          <IconButton onClick={handleClickDialog}>
            <ArrowBackIosIcon />
          </IconButton>
          <Typography variant={'subtitle2'}>Notificações</Typography>

          <IconButton onClick={handleSettingsClick}>
            <SettingsSolid size={24} className={classes.settingsIcon} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Updates updates={updates.data} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationCenter;
